import { Avatar, Button, Card, Image, Spin, Tag } from "antd";
import EditIcon from "components/Icons/EditIcon";
import ViewIcon from "components/Icons/ViewIcon";
import { RouteName } from "constants/route";
import useFleetDetail from "hooks/fleets/useFleetDetail";
import { FinancePrivilege } from "models/account";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  CarOutlined,
  UserOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

export default function FleetDetail() {
  const {
    role,
    data: { loading, data },
  } = useFleetDetail();

  return (
    <div className="w-full">
      {loading ? (
        <div
          className="w-full bg-white rounded-lg p-6"
          style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
        >
          <div className="w-full flex items-center justify-center py-20">
            <Spin />
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-col gap-6">
          {/* Header Card with Fleet Image and Basic Info */}
          <Card
            className="w-full"
            style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
          >
            <div className="w-full flex flex-col md:flex-row gap-6">
              {/* Fleet Image Section */}
              <div className="w-full md:w-1/3">
                <div className="relative">
                  <Image
                    src={data?.image}
                    alt={`${data?.brand} ${data?.model}`}
                    className="rounded-lg"
                    style={{ maxHeight: "300px", objectFit: "cover" }}
                    preview={{
                      mask: "Preview",
                    }}
                  />
                  <div className="absolute top-2 right-2">
                    <Tag color="blue" className="text-sm font-semibold px-3 py-1">
                      <CarOutlined className="mr-1" />
                      {data?.fleet_model?.name || "N/A"}
                    </Tag>
                  </div>
                </div>
              </div>

              {/* Basic Information */}
              <div className="w-full md:w-2/3">
                <div className="mb-4">
                  <h2 className="text-2xl font-bold mb-2">
                    {data?.brand} {data?.model}
                  </h2>
                  <p className="text-lg text-gray-600 font-mono font-semibold">
                    {data?.plate_number}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: data?.color || "#000" }}></span>
                      Body Color
                    </span>
                    <p className="text-lg font-semibold capitalize">{data?.color || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <CalendarOutlined />
                      Production Year
                    </span>
                    <p className="text-lg font-semibold">{data?.production_year || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">License Number</span>
                    <p className="text-lg font-semibold font-mono">{data?.license_number || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Brand Number</span>
                    <p className="text-lg font-semibold">{data?.brand_number || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Doors</span>
                    <p className="text-lg font-semibold">{data?.door_number || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Passenger Seats</span>
                    <p className="text-lg font-semibold">{data?.passenger_seat || "N/A"}</p>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {data?.has_ac ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Air Conditioning
                    </Tag>
                  ) : (
                    <Tag color="default" icon={<CloseCircleOutlined />}>
                      No AC
                    </Tag>
                  )}
                  {data?.has_tinted_windows ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Tinted Windows
                    </Tag>
                  ) : (
                    <Tag color="default" icon={<CloseCircleOutlined />}>
                      No Tinted Windows
                    </Tag>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="w-full flex items-center justify-start gap-x-4 mt-6 pt-6 border-t">
                  <Link to={RouteName.EDIT_FLEET.replace(":id", data!.id)}>
                    <Button
                      className="h-auto w-fit flex items-center px-6 py-2 gap-x-1 hover:text-black"
                      type="primary"
                    >
                      <span className="text-sm inline-block">
                        <EditIcon />
                      </span>
                      <span>Edit</span>
                    </Button>
                  </Link>
                  {FinancePrivilege.includes(role) && (
                    <Link to={RouteName.DRIVER_DETAIL.replace(":id", data!.driver_id)}>
                      <Button
                        className="h-auto w-fit flex items-center px-6 py-2 gap-x-2"
                        type="default"
                      >
                        <ViewIcon />
                        <span>View Driver</span>
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Driver Information Card */}
          <Card
            title={
              <span className="font-semibold flex items-center gap-2">
                <UserOutlined />
                Driver Information
              </span>
            }
            className="w-full"
            style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
          >
            <div className="flex items-start gap-4">
              <Avatar
                src={data?.driver?.image}
                size={80}
                icon={<UserOutlined />}
                className="border-2 border-gray-200"
              />
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">
                  {data?.driver?.first_name} {data?.driver?.middle_name} {data?.driver?.last_name}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <PhoneOutlined />
                      Phone Number
                    </span>
                    <p className="text-base font-semibold">{data?.driver?.phone || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Driver ID</span>
                    <p className="text-sm font-mono text-gray-600">{data?.driver_id || "N/A"}</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Vehicle Details Card */}
          <Card
            title={
              <span className="font-semibold flex items-center gap-2">
                <CarOutlined />
                Vehicle Details
              </span>
            }
            className="w-full"
            style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <span className="text-sm text-gray-500">Vehicle Type</span>
                <p className="text-lg font-semibold">{data?.fleet_model?.name || "N/A"}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Brand</span>
                <p className="text-lg font-semibold">{data?.brand || "N/A"}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Model</span>
                <p className="text-lg font-semibold">{data?.model || "N/A"}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Plate Number</span>
                <p className="text-lg font-semibold font-mono">{data?.plate_number || "N/A"}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">License Number</span>
                <p className="text-lg font-semibold font-mono">{data?.license_number || "N/A"}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Brand Number</span>
                <p className="text-lg font-semibold">{data?.brand_number || "N/A"}</p>
              </div>
            </div>
          </Card>

          {/* License Image Card */}
          {data?.license_image && (
            <Card
              title={<span className="font-semibold">Fleet License</span>}
              className="w-full"
              style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
            >
              <div className="w-full flex justify-center">
                <Image
                  src={data.license_image}
                  alt="Fleet License"
                  className="rounded-lg"
                  style={{ maxWidth: "500px" }}
                  preview={{
                    mask: "Preview",
                  }}
                />
              </div>
            </Card>
          )}

          {/* Additional Information Card */}
          <Card
            title={<span className="font-semibold">Additional Information</span>}
            className="w-full"
            style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="text-sm text-gray-500">Fleet ID</span>
                <p className="text-sm font-mono text-gray-600">{data?.id || "N/A"}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Created At</span>
                <p className="text-base font-medium">
                  {data?.created_at
                    ? moment(data.created_at).format("MMMM DD, YYYY [at] HH:mm")
                    : "N/A"}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Last Updated</span>
                <p className="text-base font-medium">
                  {data?.updated_at
                    ? moment(data.updated_at).format("MMMM DD, YYYY [at] HH:mm")
                    : "N/A"}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
